import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { FieldWrapper, FormSection } from '../../../shared/ui'
import { FormInput } from '../../../shared/ui/controls/FormInput'
import { Button } from '../../../shared/ui/controls/Button/Button'
import { useMailSettings, useUpdateMailSettings, useTestMailSettings } from '../../../entities/mail-setting'
import { mailSettingsFormSchema, MailSettingsFormValues } from '../model/mailSettingsFormSchema'

export function MailSettingsFormWidget() {
  const { data, isLoading } = useMailSettings()
  const updateMutation = useUpdateMailSettings()
  const testMutation = useTestMailSettings()
  const [testMessage, setTestMessage] = useState<{ ok: boolean; text: string } | null>(null)

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty, isSubmitting },
    setError,
  } = useForm<MailSettingsFormValues>({
    resolver: zodResolver(mailSettingsFormSchema),
    defaultValues: { host: '', port: 587, user: '', pass: '', from: '', werkhofleiterEmailsRaw: '' },
  })

  useEffect(() => {
    if (data) {
      reset({
        host: data.host,
        port: data.port,
        user: data.user,
        pass: '',
        from: data.from,
        werkhofleiterEmailsRaw: (data.werkhofleiterEmails ?? []).join('\n'),
      })
    }
  }, [data, reset])

  const handleFormSubmit = handleSubmit(async (values) => {
    try {
      const werkhofleiterEmails = values.werkhofleiterEmailsRaw
        .split('\n')
        .map((e) => e.trim().toLowerCase())
        .filter(Boolean)
      await updateMutation.mutateAsync({
        host: values.host,
        port: values.port,
        user: values.user ?? '',
        ...(values.pass && values.pass.trim() !== '' && { pass: values.pass }),
        from: values.from,
        werkhofleiterEmails,
      })
      reset({ ...values, pass: '', werkhofleiterEmailsRaw: werkhofleiterEmails.join('\n') })
      setTestMessage(null)
    } catch (err: any) {
      const message = err?.response?.data?.message ?? 'Fehler beim Speichern'
      setError('root', { type: 'server', message })
    }
  })

  const handleTest = async () => {
    setTestMessage(null)
    try {
      await testMutation.mutateAsync()
      setTestMessage({ ok: true, text: 'Testmail wurde gesendet.' })
    } catch (err: any) {
      const reason = err?.response?.data?.meta?.reason ?? err?.response?.data?.message ?? 'SMTP-Fehler'
      setTestMessage({ ok: false, text: `Testmail fehlgeschlagen: ${reason}` })
    }
  }

  if (isLoading) {
    return <div className="text-sm text-gray-400">Lade Einstellungen…</div>
  }

  return (
    <div className="flex flex-col gap-6">
      <form onSubmit={handleFormSubmit}>
        <FormSection title="SMTP-Konfiguration">
          <FieldWrapper label="SMTP-Host" error={errors.host?.message}>
            <FormInput name="host" control={control} placeholder="z. B. mailpro.hoststar.ch" />
          </FieldWrapper>

          <FieldWrapper label="Port" error={errors.port?.message}>
            <FormInput name="port" control={control} type="number" placeholder="587" />
          </FieldWrapper>

          <FieldWrapper label="Benutzername (optional)" error={errors.user?.message}>
            <FormInput name="user" control={control} placeholder="leer lassen für Relay ohne Auth" />
          </FieldWrapper>

          <FieldWrapper label="Passwort (optional)" error={errors.pass?.message}>
            <FormInput
              name="pass"
              control={control}
              type="password"
              placeholder={data?.isPassSet ? '••••••••••• (gespeichert)' : 'leer lassen für Relay ohne Auth'}
            />
          </FieldWrapper>

          <FieldWrapper label="Absenderadresse" error={errors.from?.message}>
            <FormInput name="from" control={control} type="email" placeholder="info@example.com" />
          </FieldWrapper>

          <FieldWrapper label="Werkhofleiter" error={errors.werkhofleiterEmailsRaw?.message}>
            <p className="text-xs text-gray-400 mb-1">
              Empfangen Aufgaben-Erinnerungen (3 Tage vor Deadline) und Deadline-Eskalationen. Eine Adresse pro Zeile.
            </p>
            <FormInput
              name="werkhofleiterEmailsRaw"
              control={control}
              as="textarea"
              rows={3}
              placeholder={'chef@werkhof.ch\nleitung@werkhof.ch'}
            />
          </FieldWrapper>

          {errors.root?.message && (
            <div className="text-[#FB2424] font-['PT_Sans'] text-[14px] italic">
              {errors.root.message}
            </div>
          )}

          <div className="flex items-center gap-4 pt-2">
            <Button type="submit" variant="surface" disabled={isSubmitting || updateMutation.isPending}>
              {isSubmitting || updateMutation.isPending ? 'Speichern…' : 'Speichern'}
            </Button>
          </div>
        </FormSection>
      </form>

      <div className="flex flex-col gap-2">
        <Button
          type="button"
          variant="neutral"
          className="w-fit"
          onClick={handleTest}
          disabled={isDirty || testMutation.isPending}
        >
          {testMutation.isPending ? 'Sende…' : 'Test senden'}
        </Button>
        {testMessage && (
          <p className={`font-['PT_Sans'] text-[14px] ${testMessage.ok ? 'text-green-600' : 'text-[#FB2424] italic'}`}>
            {testMessage.text}
          </p>
        )}
      </div>
    </div>
  )
}
